/**
 * The quick ranges above the credit report.
 *
 * <p>Weeks start on Monday. Cyprus follows the European week and the report is
 * read against Cypriot trading days, even though most customers are Israeli -
 * if that ever needs to change, it changes here and nowhere else.
 *
 * <p>Both ends are inclusive dates, not timestamps: the API treats "to" as the
 * whole of that day, so "today" is a single date on both sides rather than
 * today and tomorrow.
 */
export type RangeKey = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth';

export const RANGE_LABELS: Record<RangeKey, string> = {
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This week',
    lastWeek: 'Last week',
    thisMonth: 'This month',
    lastMonth: 'Last month'
};

/** yyyy-MM-dd in local time. toISOString() would shift the date in UTC+3. */
export const isoDate = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const addDays = (d: Date, days: number) => {
    const copy = new Date(d);
    copy.setDate(copy.getDate() + days);
    return copy;
};

/** Monday of the week containing d. getDay() is 0 for Sunday, so Sunday is 6 days in. */
const mondayOf = (d: Date) => {
    const day = d.getDay();
    return addDays(d, day === 0 ? -6 : 1 - day);
};

export const rangeFor = (key: RangeKey, today = new Date()): { from: string; to: string } => {
    switch (key) {
        case 'today':
            return { from: isoDate(today), to: isoDate(today) };
        case 'yesterday': {
            const y = addDays(today, -1);
            return { from: isoDate(y), to: isoDate(y) };
        }
        case 'thisWeek':
            return { from: isoDate(mondayOf(today)), to: isoDate(today) };
        case 'lastWeek': {
            const lastMonday = addDays(mondayOf(today), -7);
            return { from: isoDate(lastMonday), to: isoDate(addDays(lastMonday, 6)) };
        }
        case 'thisMonth':
            return { from: isoDate(new Date(today.getFullYear(), today.getMonth(), 1)), to: isoDate(today) };
        case 'lastMonth': {
            const firstOfLast = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const lastOfLast = new Date(today.getFullYear(), today.getMonth(), 0);
            return { from: isoDate(firstOfLast), to: isoDate(lastOfLast) };
        }
    }
};
